import React, { useState, useEffect, useRef } from 'react';
import { useAppSelector, useAppDispatch } from 'core/store/reduxStore';
import { setSideModal, setPreviewWidth, setSelectedRow } from 'modules/flywheel/store/incident';
import AlertSummary from './alertSummary';
import styled from 'styled-components';
import { IconGripVertical } from '@tabler/icons-react';
import { useAppTheme } from 'core/contexts/theme/theme';

interface MouseMoveEvent extends MouseEvent {
  clientX: number;
}

// Styled components for the resizable panel - matching PreviewPane styling
const PanelContainer = styled.div`
  flex-shrink: 1;
  flex-basis: 0;
  width: 0;
  height: 100%;
  display: flex;
  flex-direction: row;
  position: relative;
  overflow: hidden;
`;

const ResizeHandle = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 1rem;
  cursor: ew-resize;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ContentWrapper = styled.div`
  overflow-y: auto;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 0.75rem;

  p,
  li,
  span,
  strong,
  answer,
  a,
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    color: ${({ theme }) => theme.colors.textColor};
  }
`;

const AlertSidePanel: React.FC = () => {
  const [prevWidth, setPrevWidth] = useState(50);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const isResizing = useRef(false);
  const paneRef = useRef<HTMLDivElement>(null);
  const incidentState = useAppSelector((state) => state.incidents);
  const dispatch = useAppDispatch();
  const { apptheme } = useAppTheme();

  // Handle resize drag operations - similar to PreviewPane
  const handleMouseDown = (e: React.MouseEvent) => {
    try {
      e.preventDefault();
      isResizing.current = true;
      document.body.style.cursor = 'ew-resize';
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } catch (error) {
      console.error("Error during mouse down:", error);
    }
  };

  const handleMouseMove = (e: MouseMoveEvent) => {
    try {
      if (!isResizing.current || !paneRef.current) return;

      const parentRect = paneRef.current?.parentElement?.getBoundingClientRect();
      if (!parentRect) return;

      const newWidth = parentRect.right - e.clientX;
      
      // and up to 95% of screen width
      const clampedWidth = Math.max(newWidth, 16);

      const percent = Math.min((clampedWidth / parentRect.width) * 100, 100);
      
      dispatch(setPreviewWidth(percent));
      setPrevWidth(percent);
    } catch (error) {
      console.error("Error during resize:", error);
    }
  };

  const handleMouseUp = () => {
    try {
      isResizing.current = false;
      document.body.style.cursor = 'default';
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    } catch (error) {
      console.error("Error during mouse up:", error);
    }
  };

  const closePanel = () => {
    // Close the panel and reset the selected row
    dispatch(setSideModal(false));
    dispatch(setSelectedRow({ id: "", type: "" }));
  };
  
  // Toggle collapsed state
  const toggleCollapsed = () => {
    if (isCollapsed) {
      // If collapsed, expand to previous width or default
      dispatch(setPreviewWidth(prevWidth > 20 ? prevWidth : 30));
      setIsCollapsed(false);
    } else {
      // If expanded, collapse
      setPrevWidth(incidentState.previewWidth);
      dispatch(setPreviewWidth(2));
      setIsCollapsed(true);
    }
  };

  useEffect(() => {
    console.log("incidentState.openSideModal changed:", incidentState.openSideModal);
    console.log("incidentState.selectedId changed:", incidentState.selectedRow);
  },[incidentState.openSideModal, incidentState.selectedRow]);

  // Calculate effective width based on state
  // If previewWidth is very small (less than 3), treat as collapsed
  const isEffectivelyCollapsed = incidentState.previewWidth <= 3;
  const effectiveWidth = !incidentState.openSideModal ? 2 : (isEffectivelyCollapsed || isCollapsed ? 2 : incidentState.previewWidth);
  
  return (
    <PanelContainer 
      ref={paneRef}
      style={{
        flexGrow: effectiveWidth,
        flexShrink: 1,
        flexBasis: 0,
        width: 0,
        minWidth: '16px',
        transition: isResizing.current ? 'none' : 'flex-grow 0.2s ease-in-out',
      }}
      className={`panel-container h-full ${apptheme?.name === "dark-theme" ? "bg-zinc-900" : "bg-white"} flex flex-row relative`}
    >
      {/* Resizer handle - always visible */}
      <ResizeHandle
        className={`absolute left-0 top-0 bottom-0 w-1 cursor-ew-resize z-20 flex items-center justify-center position-sticky`}
        onMouseDown={handleMouseDown}
        onClick={isCollapsed ? toggleCollapsed : undefined}
        style={{ boxShadow: apptheme.shadows.boxShadow, width: "1rem" }}
      >
        <IconGripVertical
          size={18}
          stroke={1.5}
          color={apptheme.colors.textColor}
        />
      </ResizeHandle>

      <ContentWrapper>
        { incidentState.openSideModal === true && incidentState.selectedRow !== "" ? (
          // Show the alert summary when open and an alert is selected
          <AlertSummary 
            setOpenSideModal={closePanel}
            selectedId={incidentState.selectedRow} model_details={{type: "workflow"}} user_input={incidentState.selectedRow}          
          />
        ) : (
          // Show a message when no alert is selected
          <div className="flex h-full w-full items-center justify-center flex-col p-4 text-center">
            <div className="mb-4">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
                <polyline points="10 9 9 9 8 9"></polyline>
              </svg>
            </div>
            <h3 className="text-lg font-medium mb-2">No Alert Selected</h3>
            <p className="text-sm text-gray-500">Click on a "View →" button to see alert details here.</p>
          </div>
        )}
      </ContentWrapper>
    </PanelContainer>
  );
};

export default AlertSidePanel;